import React, { useState, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { IoClose } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { fetchPrograms, updateProgram } from "../../redux/actions/programActions";
import { useDispatch } from "react-redux";

export default function EditProgramPopup({ isOpen, onClose, onSave, programData = {} }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    eligibilityCriteria: "",
    benefits: "",
    document: null,
  });
  const [newFile, setNewFile] = useState(null); // Track new uploaded file

  useEffect(() => {
    if (programData) {
      setFormData({
        name: programData.program_name || "",
        description: programData.alumniNews_description || "",
        eligibilityCriteria: programData.eligibity_criteria || "",
        benefits: programData.program_benefits || "",
        document: programData.files || "",
      });
    }
  }, [programData]);

  if (!isOpen) return null;

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      [{ font: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      ["bold", "italic", "underline"],
      [{ align: [] }],
      ["clean"],
    ],
  };

  const formats = ["font", "header", "list", "bold", "italic", "underline", "align"];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleQuillChange = (field, content) => {
    setFormData((prevData) => ({ ...prevData, [field]: content }));
  };

  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      setNewFile(e.target.files[0]); // Update new file
      setFormData((prev) => ({ ...prev, document: e.target.files[0].name })); // Display file name
    }
  };

   // Strip HTML to validate rich text fields
   const stripHtml = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const plainDescription = stripHtml(formData.description);
  const plainEligibilityCriteria = stripHtml(formData.eligibilityCriteria);
  const plainProgramBenefits = stripHtml(formData.benefits);

  // Validate required fields
  if (!formData.name?.trim()) {
    alert("Program name is required.");
    return;
  }
  if (!plainDescription.trim()) {
    alert("Description is required.");
    return;
  }
  if (!plainEligibilityCriteria.trim()) {
    alert("Eligibility criteria is required.");
    return;
  }
  if (!plainProgramBenefits.trim()) {
    alert("Program benefits are required.");
    return;
  }
  
    const formDataToSend = new FormData();
    formDataToSend.append("program_name", formData.name);
    formDataToSend.append("description", formData.description);
    formDataToSend.append("eligibity_criteria", formData.eligibilityCriteria);
    formDataToSend.append("program_benefits", formData.benefits);
  
    // Conditionally append the file if a new one is selected
    if (newFile) {
      formDataToSend.append("files", newFile);
    }
  
    dispatch(updateProgram(formDataToSend, programData.id)); // Dispatch action with FormData
  
    alert("Program updated successfully!"); // Show alert
    onClose(); // Close the popup after update
    // window.location.reload(true)
  };
  

  return (
    <div className="fixed top-0 left-0 z-50 flex items-center justify-center w-full h-full bg-black bg-opacity-50">
      <div className="w-[1169px] p-4 bg-white rounded-lg shadow-lg max-h-[90vh]">
        <div className="overflow-y-auto max-h-[75vh] p-4">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold text-gray-700">Edit Program</h2>
            <button
              type="button"
              className="text-gray-500 border rounded-md hover:text-gray-700"
              onClick={onClose}
            >
              <IoClose className="w-6 h-6" />
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-600" htmlFor="name">
                  Programme Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
                />
              </div>
            </div>

            <div className="mt-6">
              <label className="block mb-1 text-gray-700">Description</label>
              <ReactQuill
                value={formData.description}
                theme="snow"
                modules={modules}
                formats={formats}
                onChange={(value) => handleQuillChange("description", value)}
                className="h-[150px] mb-1"
              />
            </div>

            <div className="mt-12">
              <label className="block mb-1 text-gray-700">Eligibility Criteria</label>
              <ReactQuill
                value={formData.eligibilityCriteria}
                theme="snow"
                modules={modules}
                formats={formats}
                onChange={(value) => handleQuillChange("eligibilityCriteria", value)}
                className="h-[150px] mb-1"
              />
            </div>

            <div className="mt-12">
              <label className="block mb-1 text-gray-700">Programme Benefits</label>
              <ReactQuill
                value={formData.benefits}
                theme="snow"
                modules={modules}
                formats={formats}
                onChange={(value) => handleQuillChange("benefits", value)}
                className="h-[150px] mb-1"
              />
            </div>

            <div className="mt-12">
              <label className="block mb-1 text-sm font-medium text-gray-600">Upload Document</label>
              <div className="flex items-center space-x-6">
                <input
                  type="text"
                  value={formData.document || ""}
                  readOnly
                  placeholder="No file chosen"
                  className="w-3/4 px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => document.getElementById("fileInput").click()}
                  className="px-4 py-2 font-semibold border rounded-md text-secondary-2-color border-secondary-2-color bg-opacity-10"
                >
                  Browse Doc
                </button>
              </div>
              <input
                type="file"
                id="fileInput"
                accept=".pdf,.doc,.docx"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>

            <div className="flex justify-end mt-6 space-x-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-[#A8AAAE] bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-white bg-[#92288C] rounded-md hover:bg-[#6C0B67]"
              >
                Update
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
